import type {
  ContentBundleEnvelope,
  DisasterContentBinding,
  DisasterModeFixtureBundlePayload,
  SupportedLocale,
} from "@safesphere/contracts";
import { assessPublicationEligibility } from "@safesphere/editorial";

import { getBundleStorageSnapshot } from "../offline/database.js";
import { verifyContentBundle } from "../offline/verifyBundle.js";
import type { BundleVerificationFailure } from "../offline/verifyBundle.js";
import { isAnswerPermitted } from "./machine.js";
import type { DisasterSession } from "./machine.js";

export type DisasterContentStatus = "READY" | "STALE_VERIFIED";
export type DisasterContentFallbackReason =
  | "NO_ACTIVE_BUNDLE"
  | "NO_KNOWN_GOOD_BUNDLE"
  | "BUNDLE_UNTRUSTED"
  | "BUNDLE_SCHEMA_INVALID"
  | "LOCALE_MISMATCH"
  | "PAYLOAD_INVALID"
  | "STATE_MAPPING_MISSING"
  | "REVISION_MISSING"
  | "REVISION_MISMATCH"
  | "HASH_MISMATCH"
  | "PUBLICATION_INELIGIBLE"
  | "RETIRED_OR_SUPERSEDED";

export interface ResolvedDisasterContent {
  readonly status: DisasterContentStatus;
  readonly binding: DisasterContentBinding;
  readonly bundleId: string;
  readonly locale: SupportedLocale;
  readonly revisionId: string;
  readonly eligibilityResult: "ELIGIBLE" | "STALE_BUT_PREVIOUSLY_ELIGIBLE";
  readonly fallbackSource: "ACTIVE" | "KNOWN_GOOD";
}

export type DisasterContentResolution =
  | { readonly ok: true; readonly value: ResolvedDisasterContent }
  | {
      readonly ok: false;
      readonly reason: DisasterContentFallbackReason;
      readonly verificationFailure?: BundleVerificationFailure;
      readonly bundleId?: string;
      readonly locale: SupportedLocale;
      readonly revisionId?: string;
      readonly eligibilityReasons?: readonly string[];
      readonly knownGoodAvailable: boolean;
    };

function answerPath(session: DisasterSession): readonly string[] {
  return session.history.flatMap((state) => {
    const answer = session.answers[state];
    return answer === undefined ? [] : [answer];
  });
}

function isPayload(value: unknown): value is DisasterModeFixtureBundlePayload {
  if (typeof value !== "object" || value === null) return false;
  const payload = value as Partial<DisasterModeFixtureBundlePayload>;
  const records = (value as { readonly records?: unknown }).records;
  return (
    payload.fixtureType === "DISASTER_MODE_NON_AUTHORITATIVE" &&
    Array.isArray(records) &&
    records.every((record) => {
      if (typeof record !== "object" || record === null) return false;
      const candidate = record as { readonly bindings?: unknown; readonly governance?: unknown };
      return (
        Array.isArray(candidate.bindings) &&
        typeof candidate.governance === "object" &&
        candidate.governance !== null
      );
    })
  );
}

function verificationReason(failure: BundleVerificationFailure): DisasterContentFallbackReason {
  return failure === "INCOMPATIBLE_SCHEMA" || failure === "INVALID_STRUCTURE"
    ? "BUNDLE_SCHEMA_INVALID"
    : "BUNDLE_UNTRUSTED";
}

async function resolveEnvelope(
  envelope: ContentBundleEnvelope,
  locale: SupportedLocale,
  session: DisasterSession,
  source: ResolvedDisasterContent["fallbackSource"],
  knownGoodAvailable: boolean,
  now: number,
): Promise<DisasterContentResolution> {
  const verification = await verifyContentBundle(envelope);
  if (!verification.ok) {
    return {
      ok: false,
      reason: verificationReason(verification.reason),
      verificationFailure: verification.reason,
      bundleId: envelope.body.bundleId,
      locale,
      knownGoodAvailable,
    };
  }
  if (envelope.body.locale !== locale) {
    return {
      ok: false,
      reason: "LOCALE_MISMATCH",
      bundleId: envelope.body.bundleId,
      locale,
      knownGoodAvailable,
    };
  }
  if (!isPayload(envelope.body.payload)) {
    return {
      ok: false,
      reason: "PAYLOAD_INVALID",
      bundleId: envelope.body.bundleId,
      locale,
      knownGoodAvailable,
    };
  }

  const path = answerPath(session);
  const candidates = envelope.body.payload.records.flatMap((record) =>
    record.bindings
      .filter(
        (binding) =>
          binding.state === session.current &&
          binding.answerPath.every((answer, index) => path[index] === answer),
      )
      .map((binding) => ({ binding, record })),
  );
  candidates.sort(
    (left, right) => right.binding.answerPath.length - left.binding.answerPath.length,
  );
  const matched = candidates[0];
  if (matched === undefined) {
    return {
      ok: false,
      reason: "STATE_MAPPING_MISSING",
      bundleId: envelope.body.bundleId,
      locale,
      knownGoodAvailable,
    };
  }

  const { binding, record } = matched;
  if (
    binding.screen.title.trim().length === 0 ||
    binding.screen.context.trim().length === 0 ||
    binding.screen.provenanceLabel.trim().length === 0 ||
    Number.isNaN(Date.parse(binding.screen.reviewedAt)) ||
    binding.screen.options.some((option) => !isAnswerPermitted(session.current, option.answerId))
  ) {
    return {
      ok: false,
      reason: "PAYLOAD_INVALID",
      bundleId: envelope.body.bundleId,
      locale,
      knownGoodAvailable,
    };
  }
  if (record.locale !== locale || record.governance.revision.locale !== locale) {
    return {
      ok: false,
      reason: "LOCALE_MISMATCH",
      bundleId: envelope.body.bundleId,
      locale,
      revisionId: binding.revisionId,
      knownGoodAvailable,
    };
  }
  if (record.revisionId.length === 0) {
    return {
      ok: false,
      reason: "REVISION_MISSING",
      bundleId: envelope.body.bundleId,
      locale,
      knownGoodAvailable,
    };
  }
  if (
    binding.revisionId !== record.revisionId ||
    record.governance.revision.id !== record.revisionId
  ) {
    return {
      ok: false,
      reason: "REVISION_MISMATCH",
      bundleId: envelope.body.bundleId,
      locale,
      revisionId: binding.revisionId,
      knownGoodAvailable,
    };
  }
  if (
    binding.contentHash !== record.contentHash ||
    record.governance.revision.contentHash !== record.contentHash ||
    record.governance.editorial.contentHash !== record.contentHash
  ) {
    return {
      ok: false,
      reason: "HASH_MISMATCH",
      bundleId: envelope.body.bundleId,
      locale,
      revisionId: record.revisionId,
      knownGoodAvailable,
    };
  }

  const assessment = assessPublicationEligibility(record.governance, now);
  const dispositionReasons = assessment.missingRequirements.filter(
    (reason) => reason === "RETIRED" || reason === "SUPERSEDED",
  );
  if (dispositionReasons.length > 0) {
    return {
      ok: false,
      reason: "RETIRED_OR_SUPERSEDED",
      bundleId: envelope.body.bundleId,
      locale,
      revisionId: record.revisionId,
      eligibilityReasons: dispositionReasons,
      knownGoodAvailable,
    };
  }
  const nonFreshnessFailures = assessment.missingRequirements.filter(
    (reason) => reason !== "REVIEW_STALE" && reason !== "SOURCE_STALE",
  );
  if (nonFreshnessFailures.length > 0) {
    return {
      ok: false,
      reason: "PUBLICATION_INELIGIBLE",
      bundleId: envelope.body.bundleId,
      locale,
      revisionId: record.revisionId,
      eligibilityReasons: nonFreshnessFailures,
      knownGoodAvailable,
    };
  }

  const stale = assessment.reviewFreshness === "STALE" || assessment.sourceFreshness === "STALE";
  return {
    ok: true,
    value: {
      status: stale ? "STALE_VERIFIED" : "READY",
      binding,
      bundleId: envelope.body.bundleId,
      locale,
      revisionId: record.revisionId,
      eligibilityResult: stale ? "STALE_BUT_PREVIOUSLY_ELIGIBLE" : "ELIGIBLE",
      fallbackSource: source,
    },
  };
}

export async function resolveDisasterContent(
  locale: SupportedLocale,
  session: DisasterSession,
  source: "active" | "known-good" = "active",
  now = Date.now(),
): Promise<DisasterContentResolution> {
  const snapshot = await getBundleStorageSnapshot(locale);
  const knownGoodAvailable = snapshot.knownGoodBundle !== undefined;
  const stored = source === "active" ? snapshot.activeBundle : snapshot.knownGoodBundle;
  if (stored === undefined) {
    return {
      ok: false,
      reason: source === "active" ? "NO_ACTIVE_BUNDLE" : "NO_KNOWN_GOOD_BUNDLE",
      locale,
      knownGoodAvailable,
    };
  }
  return resolveEnvelope(
    stored.bundle,
    locale,
    session,
    source === "active" ? "ACTIVE" : "KNOWN_GOOD",
    knownGoodAvailable,
    now,
  );
}
